import { CarouselProvider } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import React, { FC, useMemo, useState } from 'react';
import { useDeviceInfo, useBreakpointInfo, MediaBreakpoint } from '../../../hooks/useDeviceInfo';
import { joinStrings } from '../../../utils/string';
import { SliderWrapper } from '../../Games/Carousel';
import { CollectionGames } from '../ItvRowCollection/utils/collectionTiles';
import { ItvTopCarouselSlide } from './ItvTopCarouselSlide';
import { getPlaceholder } from '../ItvShared/collections';
import { ItvCollectionType } from '../types';
import starPartBingo from '../../../assets/Itv/star-part-bingo.svg';
import starPartSpins from '../../../assets/Itv/star-part-spins.svg';
import { useUserContext } from '../../../providers/UserProvider';
import './ItvTopCarousel.scss';

const OFFSET_TOP_CAROUSEL = -8;
const MOBILE_VISIBLE_SLIDES = 2;
const DESKTOP_VISIBLE_SLIDES = 5;
const TOP_GAMES_SLIDES_NUMBER = 10;

export type ItvTopCarouselProps = {
  title?: string;
  games?: CollectionGames;
};

export const ItvTopCarousel: FC<ItvTopCarouselProps> = ({ games, title }) => {
  const { isMobileDevice } = useDeviceInfo();
  const { theme } = useUserContext();
  const isMobile = useBreakpointInfo([MediaBreakpoint.MOBILE]);

  const [active, setActive] = useState(false);

  const visibleSlides = isMobile ? MOBILE_VISIBLE_SLIDES : DESKTOP_VISIBLE_SLIDES;

  const starPartImg = theme === 'bingo' ? starPartBingo : starPartSpins;

  const placeholder = getPlaceholder(ItvCollectionType.GAMES, 'top-games');

  const matchedTitle = title?.match(/(\D+)(\d+)([\s\S]+)/);
  const [, titleHead, titleNumber, titleText] = matchedTitle || [];

  const slideNodes = useMemo(
    () =>
      games
        ?.slice(0, TOP_GAMES_SLIDES_NUMBER)
        .map((game, index) => (
          <>
            {game?.game_id && <ItvTopCarouselSlide key={game.game_id.game_id} game={game?.game_id} index={++index} />}
          </>
        )) || [],
    [games]
  );

  const totalSlides = slideNodes?.length || 0;

  const activeHandler = (state: boolean): void => {
    setActive(state);
  };

  return (
    <div className={joinStrings(['itv-top-carousel', `itv-top-carousel--${isMobile ? 'mobile' : 'desktop'}`])}>
      <img src={starPartImg} alt="pre pick" className="itv-top-carousel__pre-pick" />
      <div className="itv-top-carousel__carousel-title">
        <div className="itv-top-carousel__carousel-title--head">{titleHead.trim().toUpperCase()}</div>
        <div className="itv-top-carousel__carousel-title--number">{titleNumber}</div>
        <div className="itv-top-carousel__carousel-title--text">{titleText.trim()}</div>
      </div>
      <div
        data-testid="carousel-wrapper"
        className={joinStrings([
          'carousel-wrapper',
          active && 'carousel-wrapper--active',
          isMobileDevice && 'carousel-wrapper--touchable',
          'carousel-wrapper--draggable',
          'carousel-wrapper--resizable',
        ])}
      >
        <CarouselProvider
          naturalSlideWidth={0}
          naturalSlideHeight={0}
          isPlaying={false}
          isIntrinsicHeight
          infinite
          touchEnabled
          dragEnabled
          visibleSlides={visibleSlides}
          totalSlides={totalSlides}
        >
          {totalSlides ? (
            <SliderWrapper
              visibleSlides={visibleSlides}
              disabled={false}
              showArrows={!isMobile}
              showDots={false}
              isMobile={isMobileDevice}
              offsetPercentage={OFFSET_TOP_CAROUSEL}
              offsetStyle="leftRight"
              touchThreshold={0}
              slides={slideNodes}
              activeHandler={activeHandler}
            />
          ) : (
            <>{placeholder}</>
          )}
        </CarouselProvider>
      </div>
    </div>
  );
};
